import { parseColor, formatOklch, getRelativeChroma } from './colorUtils.js';

// Predefined demo colors in hex format
const DEMO_COLORS = {
  Crimson: '#DC143C',
  FireBrick: '#B22222',
  DeepPink: '#FF1493',
  MediumVioletRed: '#C71585',
  Coral: '#FF7F50',
  Tomato: '#FF6347',
  OrangeRed: '#FF4500',
  DarkOrange: '#FF8C00',
  Gold: '#FFD700',
  Violet: '#EE82EE',
  MediumOrchid: '#BA55D3',
  MediumPurple: '#9370DB',
  DarkOrchid: '#9932CC',
  SlateBlue: '#6A5ACD',
  GreenYellow: '#ADFF2F',
  LimeGreen: '#32CD32',
  MediumSpringGreen: '#00FA9A',
  MediumSeaGreen: '#3CB371',
  YellowGreen: '#9ACD32',
  LightSeaGreen: '#20B2AA',
  Turquoise: '#40E0D0',
  DeepSkyBlue: '#00BFFF',
  DodgerBlue: '#1E90FF',
  MediumSlateBlue: '#7B68EE',
  RoyalBlue: '#4169E1',
  SandyBrown: '#F4A460',
  Goldenrod: '#DAA520',
  Chocolate: '#D2691E'
} as const;

type DemoColorName = keyof typeof DEMO_COLORS;

/**
 * Get a random demo color and convert it to OKLCH with relative chroma
 */
export function getRandomDemoColor(): { name: DemoColorName; oklchValue: string; hexValue: string } {
  const colorNames = Object.keys(DEMO_COLORS) as DemoColorName[];
  const randomName = colorNames[Math.floor(Math.random() * colorNames.length)];
  const hexValue = DEMO_COLORS[randomName];
  
  // Parse the hex color to get OKLCH values
  const parsed = parseColor(hexValue);
  if (!parsed || !(parsed as any).oklch) {
    // Fallback if parsing fails
    return {
      name: randomName,
      oklchValue: 'oklch(0.75 88% 58)',
      hexValue
    };
  }
  
  const oklch = (parsed as any).oklch;
  const { l, c, h, alpha } = oklch;
  
  // Use the existing getRelativeChroma function to get accurate relative chroma
  const relativeChromaDecimal = getRelativeChroma(oklch);
  const relativeChromaPercent = Math.round(relativeChromaDecimal * 100);
  
  // Round lightness to 2 decimal places for cleaner display
  const roundedLightness = Math.round(l * 100) / 100;
  
  // Round hue to whole number
  const roundedHue = Math.round(h);
  
  // Create OKLCH string with relative chroma
  const oklchValue = `oklch(${roundedLightness} ${relativeChromaPercent}% ${roundedHue})`;
  
  
  return {
    name: randomName,
    oklchValue,
    hexValue
  };
}

/**
 * Get all available demo colors for reference
 */
export function getAllDemoColors(): Array<{ name: DemoColorName; hexValue: string }> {
  return Object.entries(DEMO_COLORS).map(([name, hexValue]) => ({
    name: name as DemoColorName,
    hexValue
  }));
}
